import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { css } from "styled-components"
import { Grid, Block, Spacer } from "../../atoms/layout-primitives"
import Img from "gatsby-image"
import ContentBlock from "../../molecules/content-block"
import VariantContainer from "../../atoms/variant-container"
import IconTitleCard from "../../molecules/icon-title-card"
import { Alignments } from "../../molecules/icon-title"
import BreakpointImage from "../../atoms/breakpoint-image"
import Button from "../../atoms/button"

export default function BottomSection() {
  const query = useStaticQuery(graphql`
    query {
      routerImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/router plant.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      phonesImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/servicenow phones.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      wifiIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/icons/wifi.svg"
        }
      ) {
        publicURL
      }
      coinsIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/icons/coins.svg"
        }
      ) {
        publicURL
      }
      clockIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/icons/clock.svg"
        }
      ) {
        publicURL
      }
      targetIcon: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/icons/target.svg"
        }
      ) {
        publicURL
      }
      satelliteImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/satellite and companies.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      satelliteMobileImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/misc/satellite and companies - mobile.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  return (
    <>
      <Grid>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
        <Block
          variants={[
            css`
              grid-column: 1 / -1;
            `,
            css`
              grid-column: 3 / -3;
            `,
            css`
              grid-column: 1 / 7;
              max-width: 559px;
              max-height: 512px;
              justify-self: center;
            `,
          ]}
        >
          <Img
            fluid={query.phonesImage.childImageSharp.fluid}
            alt="Image of two phones"
          />
        </Block>
        <Block
          variants={[
            css`
              grid-column: 1 / -1;
              margin-top: 48px;
              text-align: center;
            `,
            css`
              grid-column: 2 / -2;
            `,
            css`
              grid-column: 7 / -1;
              text-align: left;
              align-self: center;
              margin-top: 0;
            `,
          ]}
        >
          <ContentBlock.H2
            title={
              <>
                Infrastructure-Free Systems <span>vs.</span> Hardware
                Positioning Systems
              </>
            }
            description={
              <>
                Infrastructure-free systems rely solely on a user's mobile
                device and the WiFi within a venue. Apple’s indoor positioning
                is an example of this as it uses your existing WiFi to achieve
                GPS-level accuracy in indoor spaces by using the radio frequency
                (RF) patterns of your WiFi access points.
                <br />
                <br />
                Infrastructure-free in this case also equates to a low setup and
                maintenance cost. Positioning systems that utilize hardware such
                as beacons can be implemented to supplement infrastructure-free
                solutions but can come with a large price tag both to set up and
                maintain over time.
              </>
            }
            titleVariants={[
              css`
                & > span {
                  color: var(--burnt-orange);
                }
                letter-spacing: 0.05em;
              `,
              css`
                letter-spacing: 0.08em;
              `,
            ]}
            bodyVariants={[
              css`
                color: var(--body-text-grey);
              `,
            ]}
          />
        </Block>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
      </Grid>

      {/* How to choose the right positioning system */}

      <VariantContainer
        variants={[
          css`
            background-color: var(--light-background);
          `,
        ]}
      >
        <Grid>
          <Spacer
            height={{ sm: "48px", md: "64px", lg: "96px" }}
            row={{ lg: "1" }}
          />
          <Block
            col={{ sm: "1 / -1", md: "3 / -3", lg: "2 / 7" }}
            variants={[
              css``,
              css``,
              css`
                grid-row: 3;
                margin-top: 64px;
              `,
            ]}
          >
            <Img
              fluid={query.routerImage.childImageSharp.fluid}
              alt="Router Image"
            />
          </Block>
          <Block
            variants={[
              css`
                grid-column: 1 / -1;
                text-align: center;
                margin-top: 48px;
              `,
              css`
                grid-column: 2 / -2;
                margin-top: 64px;
              `,
              css`
                grid-column: 2 / 7;
                text-align: left;
                grid-row: 2;
                margin-top: 0px;
              `,
            ]}
          >
            <ContentBlock.H2
              title="How to Choose the Right Positioning System"
              description="When deciding on the indoor positioning provider that will work best for your business, there are four key factors to evaluate. Outlining your goals for each of these will enable you to build the appropriate indoor positioning system with the right provider."
            />
          </Block>
          <Block
            display={{ sm: "grid", md: "grid", lg: "grid" }}
            col={{ sm: "1 / -1", md: "2 / -2", lg: "8 / 12" }}
            row={{ lg: "2 / 5" }}
            variants={[
              css`
                text-align: center;
              `,
              css``,
              css`
                text-align: left;
              `,
            ]}
          >
            <Spacer
              height={{ sm: "48px", md: "48px", lg: "0px" }}
              display={{ lg: "none" }}
            />
            <IconTitleCard
              icon={<img src={query.targetIcon.publicURL} alt="target icon" />}
              title="Accuracy Expectations"
              description="What is the realistic expectation under normal or sub-optimal conditions? For reference, the accuracy of assisted GPS is approximately three meters outdoors."
              titleAlignmentVariants={[
                Alignments["column-centered"],
                Alignments["column-centered"],
                Alignments["row-left"],
              ]}
              iconVariants={[
                css`
                  width: 32px;
                  height: 32px;
                `,
              ]}
            />
            <Spacer height={{ sm: "32px", md: "32px", lg: "48px" }} />
            <IconTitleCard
              icon={<img src={query.coinsIcon.publicURL} alt="coins icon" />}
              title="Budget"
              description="How much will it cost to implement, own, and maintain? For example, your implementation and ownership costs may be low, but the system might require a lot of maintenance at an additional cost."
              titleAlignmentVariants={[
                Alignments["column-centered"],
                Alignments["column-centered"],
                Alignments["row-left"],
              ]}
              iconVariants={[
                css`
                  width: 32px;
                  height: 32px;
                `,
              ]}
            />
            <Spacer height={{ sm: "32px", md: "32px", lg: "48px" }} />
            <IconTitleCard
              icon={<img src={query.clockIcon.publicURL} alt="clock icon" />}
              title="Response time"
              description="How long does it take for the system to respond? For example, on-device calculations are faster than server-side ones, because of the latency involved for the signal to travel there and back."
              titleAlignmentVariants={[
                Alignments["column-centered"],
                Alignments["column-centered"],
                Alignments["row-left"],
              ]}
              iconVariants={[
                css`
                  width: 32px;
                  height: 32px;
                `,
              ]}
            />
            <Spacer height={{ sm: "32px", md: "32px", lg: "48px" }} />
            <IconTitleCard
              icon={<img src={query.wifiIcon.publicURL} alt="wifi icon" />}
              title="Reliability"
              description="How well will the solution function indoors? New systems sometimes get a pass when they start out buggy, but navigation is often mission-critical."
              titleAlignmentVariants={[
                Alignments["column-centered"],
                Alignments["column-centered"],
                Alignments["row-left"],
              ]}
              iconVariants={[
                css`
                  width: 32px;
                  height: 32px;
                `,
              ]}
            />
          </Block>
          <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
        </Grid>
      </VariantContainer>

      {/* Best indoor positioning companies */}
      <Grid>
        <Spacer height={{ sm: "48px", lg: "96px" }} row={{ lg: "1" }} />
        <Block
          col={{ sm: "1 / -1", md: "2 / -2", lg: "8 / -1" }}
          row={{ lg: "2" }}
          variants={[
            css`
              text-align: center;
            `,
            null,
            css`
              text-align: left;
            `,
          ]}
        >
          <ContentBlock.H2
            title="Best Indoor Positioning Companies"
            description={
              <>
                There are many indoor positioning systems available today. To
                find the provider that is best suited for your business, it's
                important to understand what you want to use these real-time
                location systems for and ensure you have an indoor mapping
                foundation to visualize these systems on top of. Many IPS
                specialize in specific industries and can cater to the needs of
                your building and use case. Below are some of the notable
                providers available.
                <br />
                <br />
                <span style={{ fontWeight: "500" }}>
                  For infrastructure-free indoor positioning:
                  <span style={{ color: "#BF4320" }}> Apple.</span> For indoor
                  positioning that uses the Sensor Fusion approach:{" "}
                  <span style={{ color: "#BF4320" }}>
                    IndoorAtlas, Inpixon, Senion and Here Technologies.
                  </span>
                </span>
              </>
            }
          />
          <Button.ScrollLink
            to="contact-form"
            smooth
            duration={1000}
            variants={[
              css`
                margin: 32px auto 0 auto;
                grid-column: 1 / -1;
              `,
              css``,
              css`
                margin: 64px 0 0 0;
              `,
            ]}
          >
            Book A Demo
          </Button.ScrollLink>
        </Block>
        <Block
          col={{ sm: "1 / -1", md: "2 / -2", lg: "1 / 7" }}
          row={{ lg: "2" }}
          align={{sm: 'start'}}
          variants={[
            css`
              margin-top: 48px;
              box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
              border-radius: 2px;
            `,
            null,
            css`
              margin-top: 0;
            `,
          ]}
        >
          <BreakpointImage
            imageVariants={{
              sm: query.satelliteMobileImage.childImageSharp.fluid,
              md: query.satelliteImage.childImageSharp.fluid,
              lg: query.satelliteImage.childImageSharp.fluid,
            }}
          />
        </Block>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
      </Grid>
    </>
  )
}
